/**
 * TrafficCarModel - GLB-basierte NPC-Auto-Modelle
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// Traffic-Auto Typen mit GLB-Pfaden
export interface TrafficCarModelDef {
  id: string;
  name: string;
  type: 'car' | 'truck' | 'bus' | 'motorcycle' | 'suv';
  modelPath: string;
  scale: number;
  color?: THREE.Color;
  modelVariantIndex?: number; // Für Multi-Model GLBs (Packs)
}

// Verfügbare NPC-Modelle (verwenden die gleichen GLBs wie Player)
const TRAFFIC_MODELS: TrafficCarModelDef[] = [
  {
    id: 'traffic_mini',
    name: 'Mini Cooper',
    type: 'car',
    modelPath: '/assets/car-race/cars/uploads_files_6259382_Mini.glb',
    scale: 1.2,
    color: new THREE.Color(0xff0000)
  },
  {
    id: 'traffic_ford',
    name: 'Ford Focus',
    type: 'car',
    modelPath: '/assets/car-race/cars/uploads_files_6259382_Ford.glb',
    scale: 1.3,
    color: new THREE.Color(0x0066ff)
  },
  {
    id: 'traffic_dodge',
    name: 'Dodge Challenger',
    type: 'car',
    modelPath: '/assets/car-race/cars/uploads_files_6259382_Dodge.glb',
    scale: 1.4,
    color: new THREE.Color(0xffaa00)
  },
  {
    id: 'traffic_truck',
    name: 'Truck',
    type: 'truck',
    modelPath: '/assets/car-race/cars/uploads_files_6259382_BMW.glb',
    scale: 1.8,
    color: new THREE.Color(0x888800)
  },
  {
    id: 'traffic_bus',
    name: 'Bus',
    type: 'bus',
    modelPath: '/assets/car-race/cars/uploads_files_6259382_Porsche.glb',
    scale: 1.6,
    color: new THREE.Color(0x884400)
  },
  {
    id: 'traffic_motorcycle',
    name: 'Motorcycle',
    type: 'motorcycle',
    modelPath: '/assets/car-race/cars/free_1972_datsun_240k_gt.glb',
    scale: 0.9,
    color: new THREE.Color(0x44aa44)
  },
  
  // === Indian Traffic Pack - Einzelne Fahrzeuge ===
  {
    id: 'indian_sedan_1',
    name: 'Indian Sedan',
    type: 'car',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 0,
    color: new THREE.Color(0xaaaaaa)
  },
  {
    id: 'indian_hatchback_2',
    name: 'Indian Hatchback',
    type: 'car',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 1,
    color: new THREE.Color(0x6699cc)
  },
  {
    id: 'indian_suv_3',
    name: 'Indian SUV',
    type: 'suv',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 2,
    color: new THREE.Color(0x445566)
  },
  {
    id: 'indian_bus_4',
    name: 'Indian Bus',
    type: 'bus',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 3,
    color: new THREE.Color(0xffcc00)
  },
  {
    id: 'indian_truck_5',
    name: 'Indian Truck',
    type: 'truck',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 4,
    color: new THREE.Color(0x996633)
  },
  {
    id: 'indian_auto_6',
    name: 'Auto Rickshaw',
    type: 'car',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 5,
    color: new THREE.Color(0x00ff00)
  },
  {
    id: 'indian_compact_7',
    name: 'Indian Compact',
    type: 'car',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 6,
    color: new THREE.Color(0xff6600)
  },
  {
    id: 'indian_van_8',
    name: 'Indian Van',
    type: 'truck',
    modelPath: '/assets/car-race/cars/indian_car_simulator_traffic_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 7,
    color: new THREE.Color(0x663399)
  },
  
  // === Generic Passenger Pack - Einzelne Fahrzeuge ===
  {
    id: 'generic_sedan_1',
    name: 'Generic Sedan',
    type: 'car',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 0,
    color: new THREE.Color(0x3366cc)
  },
  {
    id: 'generic_coupe_2',
    name: 'Generic Coupe',
    type: 'car',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 1,
    color: new THREE.Color(0xcc3366)
  },
  {
    id: 'generic_suv_3',
    name: 'Generic SUV',
    type: 'suv',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 2,
    color: new THREE.Color(0x339966)
  },
  {
    id: 'generic_wagon_4',
    name: 'Generic Wagon',
    type: 'car',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 3,
    color: new THREE.Color(0x996633)
  },
  {
    id: 'generic_hatchback_5',
    name: 'Generic Hatchback',
    type: 'car',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 4,
    color: new THREE.Color(0xffaa33)
  },
  {
    id: 'generic_pickup_6',
    name: 'Generic Pickup',
    type: 'truck',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 5,
    color: new THREE.Color(0x666666)
  },
  {
    id: 'generic_van_7',
    name: 'Generic Van',
    type: 'truck',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 6,
    color: new THREE.Color(0x444488)
  },
  {
    id: 'generic_sports_8',
    name: 'Generic Sports Car',
    type: 'car',
    modelPath: '/assets/car-race/cars/generic_passenger_car_pack.glb',
    scale: 1.15,
    modelVariantIndex: 7,
    color: new THREE.Color(0xff0066)
  }
];

export class TrafficCarModelLoader {
  private loader = new GLTFLoader();
  private modelCache = new Map<string, THREE.Group>();
  private loadingPromises = new Map<string, Promise<THREE.Group>>();

  /**
   * Lade GLB-Modell für NPC-Auto
   */
  async loadModel(modelId: string): Promise<THREE.Group> {
    // Aus Cache laden wenn möglich
    if (this.modelCache.has(modelId)) {
      return this.modelCache.get(modelId)!.clone();
    }

    // Wenn bereits am Laden, warte auf Ergebnis
    if (this.loadingPromises.has(modelId)) {
      return (await this.loadingPromises.get(modelId)!).clone();
    }

    const modelDef = TRAFFIC_MODELS.find(m => m.id === modelId);
    if (!modelDef) {
      throw new Error(`Traffic model not found: ${modelId}`);
    }

    // Starte Laden
    const promise = this.loader
      .loadAsync(modelDef.modelPath)
      .then(gltf => {
        let scene = gltf.scene;
        
        // Wenn modelVariantIndex definiert, wähle spezifisches Sub-Modell
        if (modelDef.modelVariantIndex !== undefined) {
          const variant = this.pickVariantFromScene(scene, modelDef.modelVariantIndex);
          if (variant) {
            scene = variant;
          }
        }
        
        scene.traverse((node) => {
          const mesh = node as THREE.Mesh;
          if (mesh.isMesh) {
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            mesh.frustumCulled = true;
          }
        });
        scene.scale.setScalar(modelDef.scale);
        this.modelCache.set(modelId, scene);
        return scene;
      });

    this.loadingPromises.set(modelId, promise);

    try {
      const model = await promise;
      this.loadingPromises.delete(modelId);
      return model.clone();
    } catch (error) {
      console.warn(`Failed to load traffic model ${modelId}:`, error);
      this.loadingPromises.delete(modelId);
      // Fallback zu prozeduralem Modell
      return this.createProceduralModel(modelDef);
    }
  }

  /**
   * Wählt ein spezifisches Sub-Modell aus einem Pack-GLB
   */
  private pickVariantFromScene(scene: THREE.Group, variantIndex: number): THREE.Group | null {
    const children = scene.children.filter(c => c instanceof THREE.Mesh || c instanceof THREE.Group);
    
    if (variantIndex >= 0 && variantIndex < children.length) {
      const variant = children[variantIndex];
      const group = new THREE.Group();
      group.add(variant.clone());
      return group;
    }
    
    console.warn(`Variant index ${variantIndex} out of range (${children.length} children)`);
    return null;
  }

  /**
   * Wähle zufällig ein Traffic-Modell
   */
  getRandomModel(): TrafficCarModelDef {
    return TRAFFIC_MODELS[Math.floor(Math.random() * TRAFFIC_MODELS.length)];
  }

  /**
   * Erstelle prozedurales Fallback-Modell
   */
  private createProceduralModel(def: TrafficCarModelDef): THREE.Group {
    const group = new THREE.Group();
    let geometry: THREE.BoxGeometry;
    let color = def.color || new THREE.Color(0.5, 0.5, 0.5);

    switch (def.type) {
      case 'truck':
        geometry = new THREE.BoxGeometry(2.5, 2.5, 6);
        break;
      case 'bus':
        geometry = new THREE.BoxGeometry(2.2, 2.8, 10);
        break;
      case 'motorcycle':
        geometry = new THREE.BoxGeometry(0.7, 1.1, 2.0);
        break;
      case 'suv':
        geometry = new THREE.BoxGeometry(1.8, 1.6, 4.5);
        break;
      default:
        geometry = new THREE.BoxGeometry(1.6, 1.3, 4.0);
    }

    const material = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.5,
      metalness: 0.6
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);

    return group;
  }

  dispose(): void {
    this.modelCache.clear();
    this.loadingPromises.clear();
  }
}
